import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { setTracingConsent } from '../utils/tracingConsent';

/**
 * Cookie categories aligned with GDPR/CCPA requirements.
 *
 * - necessary: Always on. Session, auth, security, CSRF. Cannot be disabled.
 * - functional: Language preference, UI state. Opt-in under GDPR, opt-out under CCPA.
 * - analytics: Usage metrics, performance monitoring. Opt-in under GDPR, opt-out under CCPA.
 * - marketing: Ad targeting, retargeting pixels. Opt-in under GDPR, opt-out under CCPA.
 */
export interface CookiePreferences {
  necessary: true; // always true, cannot be changed
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

export type ConsentStatus = 'undecided' | 'customized' | 'accepted_all' | 'rejected_all';

interface CookieConsentState {
  /** Current user preferences. */
  preferences: CookiePreferences;
  /** Whether the user has made a choice (banner should hide). */
  consentGiven: boolean;
  /** How the user responded. */
  status: ConsentStatus;
  /** Accept all non-necessary cookies. */
  acceptAll: () => void;
  /** Reject all non-necessary cookies. */
  rejectAll: () => void;
  /** Save custom preferences. */
  savePreferences: (prefs: Omit<CookiePreferences, 'necessary'>) => void;
  /** Reset consent (show banner again). */
  resetConsent: () => void;
}

const STORAGE_KEY = 'roosting_cookie_consent';

const DEFAULT_PREFERENCES: CookiePreferences = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
};

function loadStored(): {
  preferences: CookiePreferences;
  status: ConsentStatus;
} | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.status === 'string') {
      return {
        preferences: { ...DEFAULT_PREFERENCES, ...parsed.preferences },
        status: parsed.status,
      };
    }
  } catch {
    // corrupt data — treat as undecided
  }
  return null;
}

function persist(
  preferences: CookiePreferences,
  status: ConsentStatus,
): void {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      preferences,
      status,
      updatedAt: new Date().toISOString(),
    }),
  );
}

/**
 * Remove cookies that the user has opted out of.
 * In production, this would also fire tag manager events
 * and suppress third-party scripts.
 */
function enforcePref(preferences: CookiePreferences): void {
  if (!preferences.analytics) {
    // Remove any analytics cookies (e.g., _ga, _gid)
    document.cookie.split(';').forEach((c) => {
      const name = c.trim().split('=')[0];
      if (name.startsWith('_ga') || name.startsWith('_gid')) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      }
    });
  }
  if (!preferences.marketing) {
    document.cookie.split(';').forEach((c) => {
      const name = c.trim().split('=')[0];
      if (name.startsWith('_fbp') || name.startsWith('_gcl')) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      }
    });
  }
}

const CookieConsentContext = createContext<CookieConsentState | null>(null);

export const CookieConsentProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const stored = loadStored();

  const [preferences, setPreferences] = useState<CookiePreferences>(
    stored?.preferences ?? DEFAULT_PREFERENCES,
  );
  const [status, setStatus] = useState<ConsentStatus>(
    stored?.status ?? 'undecided',
  );

  const consentGiven = status !== 'undecided';

  useEffect(() => {
    if (consentGiven) {
      enforcePref(preferences);
    }
  }, [preferences, consentGiven]);

  useEffect(() => {
    setTracingConsent(consentGiven && preferences.analytics);
  }, [preferences.analytics, consentGiven]);

  const acceptAll = useCallback(() => {
    const prefs: CookiePreferences = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(prefs);
    setStatus('accepted_all');
    persist(prefs, 'accepted_all');
  }, []);

  const rejectAll = useCallback(() => {
    const prefs: CookiePreferences = { ...DEFAULT_PREFERENCES };
    setPreferences(prefs);
    setStatus('rejected_all');
    persist(prefs, 'rejected_all');
  }, []);

  const savePreferences = useCallback(
    (custom: Omit<CookiePreferences, 'necessary'>) => {
      const prefs: CookiePreferences = { necessary: true, ...custom };
      setPreferences(prefs);
      setStatus('customized');
      persist(prefs, 'customized');
    },
    [],
  );

  const resetConsent = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setPreferences(DEFAULT_PREFERENCES);
    setStatus('undecided');
  }, []);

  return (
    <CookieConsentContext.Provider
      value={{
        preferences,
        consentGiven,
        status,
        acceptAll,
        rejectAll,
        savePreferences,
        resetConsent,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};

export function useCookieConsent(): CookieConsentState {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error(
      'useCookieConsent must be used within CookieConsentProvider',
    );
  }
  return ctx;
}
